import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import OrgScopedDomainsContext from 'bundles/unified-home-common/contexts/org-scoped/OrgScopedDomainsContext';
import type { OrgScopedDomain } from 'bundles/unified-home-common/contexts/org-scoped/OrgScopedDomainsContext';
import OrgCurriculumDomainsQuery from 'bundles/unified-home-common/queries/OrgCurriculumDomainsQuery.graphql';
import type {
  OrgCurriculumDomainsQueryQuery,
  OrgCurriculumDomainsQueryQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/OrgCurriculumDomainsQuery';

/**
 * Fetches curriculum domains for the current org via EntCurriculum.getOrgCurriculumDomains.
 * Provides domain data (id, name, slug) to the DomainPills renderer.
 *
 * Name and slug are read from the `domainName`/`slug` scalars on the curriculum domain rather
 * than from the federated `domain` entity, so the query plan does not need a second fetch to
 * discovery-domains-application. Only the opaque `domain.id` comes from the federated entity.
 */
const OrgScopedDomainsProvider = ({ children }: React.PropsWithChildren) => {
  const { orgSlug } = useParams();

  const { data, loading } = useQuery<OrgCurriculumDomainsQueryQuery, OrgCurriculumDomainsQueryQueryVariables>(
    OrgCurriculumDomainsQuery,
    {
      variables: { orgSlug: orgSlug ?? '' },
      skip: !orgSlug,
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
      ssr: false,
    }
  );

  const domains: OrgScopedDomain[] = React.useMemo(() => {
    const elements = data?.EntCurriculum?.getOrgCurriculumDomains?.elements;
    if (!elements) return [];
    return elements
      .filter((element) => Boolean(element.domain.id))
      .map((element) => ({ id: element.domain.id, name: element.domainName, slug: element.slug }));
  }, [data]);

  const contextValue = React.useMemo(() => ({ domains, loading }), [domains, loading]);

  return <OrgScopedDomainsContext.Provider value={contextValue}>{children}</OrgScopedDomainsContext.Provider>;
};

export default OrgScopedDomainsProvider;
